import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UseFormSetError } from "react-hook-form";
import { Response } from "@/models/Response";
import { Response as ContinenteResponse } from "@/models/continente-credentials/common/Response";
import { UserContext } from "@/contexts/continente-credentials/UserContext";
import { useCallback, useContext } from "react";

export interface ErrorHandlerConfig {
    setError?: UseFormSetError<any>;
    setAdminBlocked?: (blocked: boolean) => void;
    customErrorMappings?: Record<string, ErrorMapping>;
    onSuccess?: (response: any) => void;
}

export interface ErrorMapping {
    field: string;
    translationKey: string;
    setAdminBlocked?: boolean;
}

export interface ApiResponseHandlerOptions {
    errorMappings?: Record<string, ErrorMapping>;
    setAdminBlocked?: (blocked: boolean) => void;
}

type ApiResponse<T> = Response<T> | ContinenteResponse<T>;

const DEFAULT_ERROR_MAPPINGS: Record<string, ErrorMapping> = {
    "100000": {
        field: "password",
        translationKey: "srt.password_validation.error_input_error",
    },
    "100002": {
        field: "password",
        translationKey: "srt.password_validation.error_input_error",
    },
    "100003": {
        field: "cardNumber",
        translationKey: "card_association.invalid_loyaltycard",
    },
    "100004": {
        field: "code",
        translationKey: "password_recover.invalid_code",
    },
    "100005": {
        field: "cardNumber",
        translationKey: "card_association.invalid_loyaltycard",
    },
    "100006": {
        field: "cardNumber",
        translationKey: "card_association.invalid_loyaltycard",
    },
    "100008": {
        field: "password",
        translationKey: "password_definition.password_invalid",
    },
    "100010": {
        field: "submitError",
        translationKey: "otp_validation.resend_time_error",
    },
    "100013": {
        field: "pin",
        translationKey: "pin_validation.not_correct_pin_message"
    },
    "100015": {
        field: "submitError",
        translationKey: "password_recover.invalid_nonce"
    },
    "100016": {
        field: "submitError",
        translationKey: "password_recover.invalid_nonce"
    },
    "100017": {
        field: "blocked",
        translationKey: "login_password.temp_blocked",
        setAdminBlocked: false,
    },
    "100018": {
        field: "password",
        translationKey: "password_definition.password_invalid",
    },
    "100020": {
        field: "cardNumber",
        translationKey: "pin_recover.invalid_card",
    },
    "100025": {
        field: "blocked",
        translationKey: "login_password.other_blocked",
        setAdminBlocked: false,
    },
    "100028": {
        field: "submitError",
        translationKey: "common.invalid_google_recaptcha_token",
    },
    "100033": {
        field: "identifierToAssociate",
        translationKey: "site_credentials.phonenumber_association.definition.invalid_input_same_number",
    },
    "100034": {
        field: "cardNumber",
        translationKey: "site_credentials.loyaltycard_association.definition.error",
    },
    "100035": {
        field: "submitError",
        translationKey: "site_credentials.loyaltycard_association.definition.error",
    },
    "100036": {
        field: "submitError",
        translationKey: "common.error_invalid_pt_number",
    },
    "300001": {
        field: "submitError",
        translationKey: "common.unexpected_error",
    },
    "300002": {
        field: "submitError",
        translationKey: "common.unexpected_error",
    },
    "300007": {
        field: "submitError",
        translationKey: "identification_verification.input_error",
    },
    "300009": {
        field: "submitError",
        translationKey: "site_credentials.email_association.email_already_registered",
    },
    "300010": {
        field: "submitError",
        translationKey: "password_definition.password_repeated",
    },
    "300011": {
        field: "submitError",
        translationKey: "common.unexpected_error",
    },
    "300013": {
        field: "submitError",
        translationKey: "common.unexpected_error",
    },
    "300012": {
        field: "submitError",
        translationKey: "password_definition.password_repeated",
    },
    "300130": {
        field: "submitError",
        translationKey: "common.unexpected_error",
    },
    "300137": {
        field: "submitError",
        translationKey: "phonenumber_association.invalid_association",
    },
    "300138": {
        field: "submitError",
        translationKey: "phonenumber_association.invalid_association",
    },
    "300139": {
        field: "submitError",
        translationKey: "email_association.invalid_association",
    },
    "500001": {
        field: "submitError",
        translationKey: "site_credentials.otp_validation.invalid_dws",
    },
    "400001": {
        field: "submitError",
        translationKey: "consent.declined_error",
    },
};

export const useApiResponseHandler = (options: ApiResponseHandlerOptions = {}) => {
    const history = useHistory();
    const { t } = useTranslation();
    const userContext = useContext(UserContext);

    const handleResponse = useCallback(<T,>(
        response: ApiResponse<T> | null,
        config?: ErrorHandlerConfig,
        error?: Error | null
    ) => {
        const { setError, setAdminBlocked, customErrorMappings = {} } = config || {};
        const errorMappings = { ...DEFAULT_ERROR_MAPPINGS, ...customErrorMappings };

        if (error || !response) {
            setError && setError("submitError", {
                message: t("common.unexpected_error"),
            });
            return;
        }

        switch (response.kind) {
            case "success":
                if (config?.onSuccess) {
                    config.onSuccess(response);
                } else if (response.value && typeof response.value === 'object' && 'nextStep' in response.value) {
                    const nextStep = (response.value as any).nextStep;
                    if (nextStep && typeof nextStep === 'string') {
                        history.push(nextStep.substring(4), response.value);
                    }
                }
                break;

            case "error":
                if (!setError) break;
                const errorCode = response.code;
                const errorMapping = errorMappings[errorCode];

                if (errorMapping) {
                    setError(errorMapping.field, {
                        message: t(errorMapping.translationKey),
                    });

                    if (errorMapping.setAdminBlocked !== undefined && setAdminBlocked) {
                        setAdminBlocked(errorMapping.setAdminBlocked);
                    }
                } else {
                    setError("submitError", {
                        message: t("common.unexpected_error"),
                    });
                }
                break;

            case "unauthorized":
                if (setError) {
                    setError("submitError", {
                        message: t("common.unauthorized_error"),
                    });
                }
                break;

            case "redirect":
                if ('url' in response) {
                    window.location.href = response.url;
                }
                break;

            case "unexpected_error":
            default:
                if (setError) {
                    setError("submitError", {
                        message: t("common.unexpected_error"),
                    });
                }
                break;
        }
    }, [history, t]);

    return { handleResponse };
};


export const handleApiResponse = <T>(
    response: ApiResponse<T>,
    config: ErrorHandlerConfig & {
        t: (key: string) => string;
        history?: any;
        onSuccess?: (response: T) => void;
    }
) => {
    const { setError, setAdminBlocked, customErrorMappings = {}, t, history, onSuccess } = config;
    const errorMappings = { ...DEFAULT_ERROR_MAPPINGS, ...customErrorMappings };

    switch (response.kind) {
        case "success":
            if (onSuccess) {
                onSuccess(response.value);
            } else if (history && response.value && typeof response.value === 'object' && 'nextStep' in response.value) {
                // Default behavior for responses with nextStep
                const nextStep = (response.value as any).nextStep;
                if (nextStep && typeof nextStep === 'string') {
                    history.push(nextStep.substring(4), response.value);
                }
            }
            break;

        case "error":
            const errorCode = response.code;
            const errorMapping = errorMappings[errorCode];

            if (errorMapping) {
                setError && setError(errorMapping.field, {
                    message: t(errorMapping.translationKey),
                });

                if (errorMapping.setAdminBlocked !== undefined && setAdminBlocked) {
                    setAdminBlocked(errorMapping.setAdminBlocked);
                }
            } else {
                setError && setError("submitError", {
                    message: t("common.unexpected_error"),
                });
            }
            break;

        case "unauthorized":
            setError && setError("submitError", {
                message: t("common.unauthorized_error"),
            });
            break;

        case "redirect":
            if ('url' in response) {
                window.location.href = response.url;
            }
            break;

        case "unexpected_error":
        default:
            setError && setError("submitError", {
                message: t("common.unexpected_error"),
            });
            break;
    }
};
